import fetch from 'isomorphic-fetch';

import { STOREFRONT_API_URL, STOREFRONT_API_ACCESS_TOKEN } from './config';

/**
 * Login customer using multipass token
 * @param {string} multipassToken
 */

// eslint-disable-next-line
export const createCustomerAccessTokenWithMultipass = async(multipassToken) => {
	const query = `
		mutation customerAccessTokenCreateWithMultipass($multipassToken: String!) {
			customerAccessTokenCreateWithMultipass(multipassToken: $multipassToken) {
				customerAccessToken {
					accessToken
					expiresAt
				}
				customerUserErrors {
					code
					field
					message
				}
			}
		}
	`.trim();

	const variables = {
		multipassToken,
	};

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		})
		.then(data => {
			return data;
		});
};

/**
 * Fetch customer data
 * @param {string} customerAccesstoken
 */
export const getCustomerData = customerAccessToken => {
	const query = `
		{
			customer(customerAccessToken: "${ customerAccessToken }") {
				shopify_id: id
				firstName
				lastName
				email
				phone
				defaultAddress {
					id
				}
				orders(first: 250) {
					edges {
						node {
							id
							orderNumber
							processedAt
							financialStatus
							fulfillmentStatus
							totalPrice {
								amount
								currencyCode
							}
							subtotalPrice {
								amount
								currencyCode
							}
							totalShippingPrice {
								amount
								currencyCode
							}
							totalTax {
								amount
								currencyCode
							}
							lineItems(first: 250) {
								edges {
									node {
										title
										currentQuantity
										variant {
											id
											title
											sku
											price {
												amount
												currencyCode
											}
										}
										discountedTotalPrice {
											amount
											currencyCode
										}
									}
								}
							}
							shippingAddress {
								name
								address1
								address2
								city
								province
								country
								zip
								phone
							}
						}
					}
				}
				addresses(first: 250) {
					edges {
						node {
							id
							name
							firstName
							lastName
							company
							address1
							address2
							city
							province
							provinceCode
							country
							countryCode
							zip
							phone
						}
					}
				}
			}
		}
	`.trim();

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
		}),
	})
		.then(res => {
			if (!res.ok) throw new Error();
			return res.json();
		})
		.then(data => {
			return data;
		})
		.catch(err => {
			window.console.error('retrieve customer shopify data failed:', err);
			throw new Error('Failed to retrieve customer shopify data');
		});
};

/**
 * Fetch customer studios
 * @param {email} email
 */
export const getCustomerStudios = async email => {
	const fragment = `
		fragment studios on ParticipantInfoCollection {
			items {
		  		sys {
					id
		 		}
				businessName
				contactName
				email
				streetAddress1
				streetAddress2
				city
				state
				postalCode
				country
				telephoneBusiness
				websiteUrl
				publish
				approved
				location {
					lat
					lon
				}
				filters
			}
	  	}
	`.trim();

	const allQuery = `
		query All ($email: String!) {
			participantInfoCollection(preview: true, limit: 100, where: {email: $email}) {
		  		total
		  		...studios
			}
	  	}	 
		${ fragment } 
	`.trim();

	const publishedQuery = `
		query Published ($email: String!) {
			participantInfoCollection(limit: 25, where: {email: $email}) {
				total
			  	...studios
			}
	  	}	 
		${ fragment } 
	`.trim();

	const variables = {
		email,
	};

	const allStudios = fetch(`https://graphql.contentful.com/content/v1/spaces/${ process.env.GATSBY_CONTENTFUL_SPACE_ID }/environments/${ process.env.GATSBY_CONTENTFUL_ENV }`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			Authorization: `Bearer ${ process.env.GATSBY_CONTENTFUL_PREVIEW_TOKEN }`,
		},
		body: JSON.stringify({
			query: allQuery,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		});

	const publishedStudios = fetch(`https://graphql.contentful.com/content/v1/spaces/${ process.env.GATSBY_CONTENTFUL_SPACE_ID }/environments/${ process.env.GATSBY_CONTENTFUL_ENV }`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			Authorization: `Bearer ${ process.env.GATSBY_CONTENTFUL_TOKEN }`,
		},
		body: JSON.stringify({
			query: publishedQuery,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		});

	try {
		const [all, published] = await Promise.all([allStudios, publishedStudios]);
		const allItems = all.data.participantInfoCollection?.items;
		const publishedItems = published.data.participantInfoCollection?.items;

		return allItems.map(item => {
			const { sys: { id } } = item;
			const isPublished = publishedItems.some(publishedItem => publishedItem.sys.id === id);
			return {
				...item,
				inProgress: !isPublished,
			};
		}, []);
	} catch (err) {
		window.console.log(err);
		return [];
	}
};

/**
 * Create new customer address
 * @param {object} address
 * @param {string} customerAccessToken
 */
export const customerAddressCreate = async(address, customerAccessToken) => {
	const query = `
		mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
			customerAddressCreate(
				customerAccessToken: $customerAccessToken
				address: $address
			) {
				customerAddress {
					id
					name
					firstName
					lastName
					company
					address1
					address2
					city
					province
					provinceCode
					country
					countryCode
					zip
					phone
				}
				customerUserErrors {
					code
					field
					message
				}
			}
		}
	`.trim();

	const variables = {
		customerAccessToken,
		address: {
			...address,
		},
	};

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		})
		.then(data => {
			return data;
		});
};

/**
 * Edit customer address
 * @param {string} id
 * @param {object} address
 * @param {string} customerAccessToken
 */
export const updateCustomerAddress = async(id, address, customerAccessToken) => {
	const query = `
		mutation customerAddressUpdate($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
			customerAddressUpdate(
				customerAccessToken: $customerAccessToken
				id: $id
				address: $address
			) {
				customerAddress {
					id
					name
					firstName
					lastName
					company
					address1
					address2
					city
					province
					provinceCode
					country
					countryCode
					zip
					phone
				}
				customerUserErrors {
					code
					field
					message
				}
			}
		}
	`.trim();

	const variables = {
		id,
		address,
		customerAccessToken,
	};

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		})
		.then(data => {
			return data;
		});
};

/**
 * Defalault customer address
 * @param {string} id
 * @param {string} customerAccessToken
 */
export const updateCustomerDefaultAddress = async(addressId, customerAccessToken) => {
	const query = `
		mutation customerDefaultAddressUpdate($customerAccessToken: String!, $addressId: ID!) {
			customerDefaultAddressUpdate(
				customerAccessToken: $customerAccessToken
				addressId: $addressId
			) {
				customer {
					id
					defaultAddress {
						id
					}
					addresses(first: 250) {
						edges {
							node {
								id
								name
								firstName
								lastName
								company
								address1
								address2
								city
								province
								provinceCode
								country
								countryCode
								zip
								phone
							}
						}
					}
				}
				customerUserErrors {
					code
					field
					message
				}
			}
		}
	`.trim();

	const variables = {
		addressId,
		customerAccessToken,
	};

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		})
		.then(data => {
			return data;
		});
};

/**
 * Delete customer address
 * @param {string} id
 * @param {string} customerAccessToken
 */
export const deleteCustomerAddress = async(id, customerAccessToken) => {
	const query = `
		mutation customerAddressDelete($id: ID!, $customerAccessToken: String!) {
			customerAddressDelete(id: $id, customerAccessToken: $customerAccessToken) {
				customerUserErrors {
					code
					field
					message
				}
				deletedCustomerAddressId
			}
		}
	`.trim();

	const variables = {
		id,
		customerAccessToken,
	};

	return fetch(STOREFRONT_API_URL, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
		},
		body: JSON.stringify({
			query,
			variables,
		}),
	})
		.then(res => {
			return res.json();
		})
		.then(data => {
			return data;
		});
};
